export type ButtonVariant =
  'primary' | 'success' | 'secondary' | 'danger' | 'warning' | 'info' | 'dark' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

/** Bootstrap 4 `.btn-*` colours: solid, hover darkened ~10%. Primary and success share the brand green. */
const SOLID: Record<ButtonVariant, string> = {
  primary: 'border-brand bg-brand text-white hover:border-brand-border hover:bg-brand-hover',
  success: 'border-brand bg-brand text-white hover:border-brand-border hover:bg-brand-hover',
  secondary:
    'border-btn-secondary-border bg-white text-ink hover:border-[#adadad] hover:bg-btn-secondary-hover',
  danger: 'border-danger bg-danger text-white hover:border-danger-hover hover:bg-danger-hover',
  warning: 'border-warning bg-warning text-white hover:border-warning-hover hover:bg-warning-hover',
  info: 'border-info bg-info text-white hover:border-info-hover hover:bg-info-hover',
  dark: 'border-ink bg-ink text-white hover:bg-black',
  link: 'border-transparent bg-transparent text-brand hover:text-brand-dark hover:underline',
};

const OUTLINE: Record<ButtonVariant, string> = {
  primary: 'border-brand bg-transparent text-brand hover:bg-brand hover:text-white',
  success: 'border-brand bg-transparent text-brand hover:bg-brand hover:text-white',
  secondary: 'border-btn-secondary-border bg-transparent text-ink hover:bg-btn-secondary-hover',
  danger: 'border-danger bg-transparent text-danger hover:bg-danger hover:text-white',
  warning: 'border-warning bg-transparent text-warning hover:bg-warning hover:text-white',
  info: 'border-info bg-transparent text-info hover:bg-info hover:text-white',
  dark: 'border-ink bg-transparent text-ink hover:bg-ink hover:text-white',
  link: SOLID.link,
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'gap-1 rounded-bs-sm px-2 py-1 text-xs leading-[1.5]',
  md: 'gap-1.5 rounded-bs px-4 py-2 text-sm leading-[1.25]',
  lg: 'gap-2 rounded-bs-lg px-6 py-3 text-base leading-[1.25]',
};

const ICON_ONLY: Record<ButtonSize, string> = {
  sm: 'rounded-bs-sm px-2 py-1 text-xs leading-[1.5]',
  md: 'rounded-bs px-3 py-2 text-sm leading-[1.25]',
  lg: 'rounded-bs-lg px-4 py-3 text-base leading-[1.25]',
};

/**
 * The classes of a legacy `.btn`, for elements that are not a `Button` (router links, file
 * labels). Size `md` is ~36px high, `sm` ~28px.
 */
export function buttonClass({
  variant = 'secondary',
  size = 'md',
  outline = false,
  block = false,
  iconOnly = false,
  wrap = false,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  outline?: boolean;
  /** Full width (`btn-block`). */
  block?: boolean;
  iconOnly?: boolean;
  /** Let a long label wrap instead of overflowing. */
  wrap?: boolean;
} = {}): string {
  // Exactly one `display` and one `white-space` class: two of a kind would be settled by
  // Tailwind's output order, not by intent.
  return [
    block ? 'flex w-full' : 'inline-flex',
    wrap ? 'whitespace-normal' : 'whitespace-nowrap',
    'items-center justify-center border font-normal no-underline transition-all duration-200 select-none',
    'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-input-focus',
    'disabled:cursor-not-allowed disabled:opacity-65 aria-disabled:cursor-not-allowed aria-disabled:opacity-65',
    iconOnly ? ICON_ONLY[size] : SIZES[size],
    outline ? OUTLINE[variant] : SOLID[variant],
  ]
    .filter(Boolean)
    .join(' ');
}
